import type { AppStrings } from './types.js';
import { en } from './en.js';
import { ru } from './ru.js';
import { ar } from './ar.js';
import { be } from './be.js';
import { ca } from './ca.js';
import { zhHans } from './zh-hans.js';
import { zhHant } from './zh-hant.js';
import { nl } from './nl.js';
import { fi } from './fi.js';
import { fr } from './fr.js';
import { de } from './de.js';
import { he } from './he.js';
import { hi } from './hi.js';
import { id } from './id.js';
import { it } from './it.js';
import { ja } from './ja.js';
import { kk } from './kk.js';
import { ko } from './ko.js';
import { ms } from './ms.js';
import { fa } from './fa.js';
import { pl } from './pl.js';
import { ptBR } from './pt-br.js';
import { ptPT } from './pt-pt.js';
import { es } from './es.js';
import { tr } from './tr.js';
import { uk } from './uk.js';
import { uz } from './uz.js';

/**
 * Реестр поддерживаемых языков интерфейса (work-026).
 *
 * Набор — официальные языки интерфейса Telegram (≈27). `code` — IETF-тег,
 * совпадает с тем, что приходит в `language_code` initData (или нормализуется
 * к нему в detectLocale). Единственный источник правды для:
 *   - выпадающего выбора языка (SettingsScreen) — `native`-подпись;
 *   - валидации API (schemas/locale → LocaleSchema = z.enum(LOCALE_CODES));
 *   - определения языка (detectLocale).
 *
 * ВАЖНО разделение:
 *   - `SUPPORTED_LOCALES` — то, что МОЖНО выбрать (27 языков, «витрина»).
 *   - `BUNDLES` — языки, для которых УЖЕ есть готовый перевод. Недостающие
 *     при `getStrings` падают на английский (en) — приложение не ломается,
 *     пока перевод не завезён. По мере добавления <code>.ts его регистрируют
 *     здесь, в BUNDLES.
 */
export interface LocaleMeta {
  /** IETF-тег (как в language_code). */
  code: string;
  /** Самоназвание языка — показывается в списке выбора. */
  native: string;
  /** Английское название — для справки/логов. */
  english: string;
  /** Письмо справа налево (ar/fa/he) — влияет на dir документа. */
  rtl?: boolean;
}

export const SUPPORTED_LOCALES = [
  { code: 'en', native: 'English', english: 'English' },
  { code: 'ru', native: 'Русский', english: 'Russian' },
  { code: 'ar', native: 'العربية', english: 'Arabic', rtl: true },
  { code: 'be', native: 'Беларуская', english: 'Belarusian' },
  { code: 'ca', native: 'Català', english: 'Catalan' },
  { code: 'zh-hans', native: '简体中文', english: 'Chinese (Simplified)' },
  { code: 'zh-hant', native: '繁體中文', english: 'Chinese (Traditional)' },
  { code: 'nl', native: 'Nederlands', english: 'Dutch' },
  { code: 'fi', native: 'Suomi', english: 'Finnish' },
  { code: 'fr', native: 'Français', english: 'French' },
  { code: 'de', native: 'Deutsch', english: 'German' },
  { code: 'he', native: 'עברית', english: 'Hebrew', rtl: true },
  { code: 'hi', native: 'हिन्दी', english: 'Hindi' },
  { code: 'id', native: 'Bahasa Indonesia', english: 'Indonesian' },
  { code: 'it', native: 'Italiano', english: 'Italian' },
  { code: 'ja', native: '日本語', english: 'Japanese' },
  { code: 'kk', native: 'Қазақ', english: 'Kazakh' },
  { code: 'ko', native: '한국어', english: 'Korean' },
  { code: 'ms', native: 'Bahasa Melayu', english: 'Malay' },
  { code: 'fa', native: 'فارسی', english: 'Persian', rtl: true },
  { code: 'pl', native: 'Polski', english: 'Polish' },
  { code: 'pt-br', native: 'Português (Brasil)', english: 'Portuguese (Brazil)' },
  { code: 'pt-pt', native: 'Português (Portugal)', english: 'Portuguese (Portugal)' },
  { code: 'es', native: 'Español', english: 'Spanish' },
  { code: 'tr', native: 'Türkçe', english: 'Turkish' },
  { code: 'uk', native: 'Українська', english: 'Ukrainian' },
  { code: 'uz', native: 'Oʻzbek', english: 'Uzbek' },
] as const satisfies readonly LocaleMeta[];

/** Union всех поддерживаемых кодов языка. */
export type Locale = (typeof SUPPORTED_LOCALES)[number]['code'];

/** Плоский список кодов (для z.enum и Set-проверок). */
export const LOCALE_CODES = SUPPORTED_LOCALES.map((l) => l.code) as unknown as [
  Locale,
  ...Locale[],
];

/** Языки с письмом справа налево. */
export const RTL_LOCALES = new Set<Locale>(['ar', 'fa', 'he']);

/**
 * Реестр готовых переводов. Недостающие коды → fallback на en в getStrings.
 * По мере добавления файла <code>.ts добавляй сюда: `import { xx } from './xx.js'`.
 */
export const BUNDLES: Partial<Record<Locale, AppStrings>> = {
  en,
  ru,
  ar,
  be,
  ca,
  'zh-hans': zhHans,
  'zh-hant': zhHant,
  nl,
  fi,
  fr,
  de,
  he,
  hi,
  id,
  it,
  ja,
  kk,
  ko,
  ms,
  fa,
  pl,
  'pt-br': ptBR,
  'pt-pt': ptPT,
  es,
  tr,
  uk,
  uz,
};
